import { useSignupIndividualMutation, useSignupMutation } from "@ac-expo/api";
import { AUTH_ERROR_MESSAGES, SignupBody, SignupIndividualBody } from "@ac-expo/api-types";
import { Button, Form, FormCheckbox, FormInput, FormPasswordInput } from "@ac-expo/ui-components";
import { passwordChecks } from "@ac-expo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import OwnImage from "@ui/components/ui/event/OwnImage";
import mailConfirmationStore from "@web-app/src/services/MailConfirmationStore";
import { AuthModalPossibilities } from "@web-app/src/types/authModalPossibilities";
import { useRouter } from "next/router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { defaultSignupValues, SignupForm, SignupSchema } from "../../schema/signUp.schema";
import { PasswordCheck, ProfessionalFields, RoleSelector, TermsLabel } from "./components";

type Props = {
  onSwitchSteps: (step: AuthModalPossibilities) => void;
  onClose: () => void;
  baseEmail?: string | null;
  goto?: string | null;
  isFromTickets?: boolean;
};

const EMAIL_ALREADY_USED_ERROR = "Il existe déjà un compte associé à cette adresse e-mail.";

export function Signup({ onSwitchSteps, onClose, baseEmail, goto, isFromTickets }: Props) {
  const router = useRouter();

  const [isVATVisible, setIsVATVisible] = useState(true);
  const [isSiretVisible, setIsSiretVisible] = useState(true);

  const { mutate: signupMutate, isError: isSignupError } = useSignupMutation();
  const { mutate: signupIndividualMutate, isError: isSignupIndividualError } =
    useSignupIndividualMutation();

  const form = useForm<SignupForm>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      ...defaultSignupValues,
      ...(baseEmail ? { email: baseEmail } : {}),
    },
  });

  const isProfessional = form.watch("isProfessional");

  const passwordError = form.formState.errors.password?.message;

  function onSubmit(values: SignupForm) {
    if (values.isProfessional) {
      const payload: SignupBody = {
        ...values,
        roles: [],
      };
      signupMutate(payload, {
        onSuccess: (response) => {
          mailConfirmationStore.setUserEmail(values.email);
          mailConfirmationStore.setModalVisible(true);
          onClose();
          if (goto) router.push(goto);
        },
        onError: (error) => {
          if (error.message === AUTH_ERROR_MESSAGES.EMAIL_ALREADY_USED) {
            form.setError("email", {
              message: EMAIL_ALREADY_USED_ERROR,
            });
          }
        },
      });
    } else {
      const payload: SignupIndividualBody = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        acceptedTerms: values.acceptedTerms,
      };
      signupIndividualMutate(payload, {
        onSuccess: (response) => {
          mailConfirmationStore.setUserEmail(response.email);
          mailConfirmationStore.setModalVisible(true);
          onClose();
        },
        onError: (error) => {
          if (error.message === AUTH_ERROR_MESSAGES.EMAIL_ALREADY_USED) {
            form.setError("email", {
              message: EMAIL_ALREADY_USED_ERROR,
            });
          }
        },
      });
    }
  }

  // Reset pro fields when not professional
  if (!isProfessional) {
    form.setValue("vat", undefined);
    form.setValue("siret", undefined);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-2">
          <OwnImage
            src="/images/logos/logo_connect_events_vertical.png"
            alt="logo avec nom Connect'Events vertical"
            className="static h-28 w-auto"
          />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-extrabold text-black">Créer un compte</h1>
        </div>
        {isFromTickets && (
          <div className="flex w-full flex-col items-center gap-2">
            <p className="w-4/5 text-center">
              Afin de pouvoir récupérer vos titres d&apos;accès, vous devez posséder un compte sur
              Connect&apos;Events.
            </p>
          </div>
        )}
        <div className="grid w-full grid-cols-2 gap-4">
          <FormInput
            control={form.control}
            name="firstName"
            label="Prénom"
            placeholder="Prénom"
            type="text"
            maxLength={64}
          />
          <FormInput
            control={form.control}
            name="lastName"
            label="Nom"
            placeholder="Nom"
            type="text"
            maxLength={64}
          />
        </div>
        <div className="w-full space-y-4">
          <RoleSelector
            control={form.control}
            name="isProfessional"
            hasError={!!form.getFieldState("isProfessional").error}
          />
          {isProfessional && (
            <ProfessionalFields
              control={form.control}
              setValue={form.setValue}
              siretName="siret"
              vatName="vat"
              businessNameName="businessName"
              countryName="country"
              isSiretVisible={isSiretVisible}
              isVATVisible={isVATVisible}
              onSiretVisibilityChange={setIsSiretVisible}
              onVATVisibilityChange={setIsVATVisible}
            />
          )}
          <div className="w-full space-y-3">
            <FormInput
              className="mt-1.5"
              control={form.control}
              name="email"
              label="Email"
              placeholder="olivia@mail.com"
              type="email"
            />
            <FormPasswordInput
              className="grow"
              control={form.control}
              name="password"
              label="Mot de passe"
              placeholder="............."
              onChange={(e) => {
                form.setValue("password", e.target.value);
                form.trigger("password");
              }}
              hasMessage={false}
            />
            <div className="space-y-3">
              <h3 className="text-sm">Le mot de passe doit contenir les éléments suivants :</h3>
              <div className="flex flex-col gap-2">
                {passwordChecks.map((check) => (
                  <PasswordCheck
                    key={check.errorType}
                    desc={check.desc}
                    errorType={check.errorType}
                    toDisplay={!!form.getValues().password}
                    passwordError={passwordError}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <FormCheckbox
          control={form.control}
          name="acceptedTerms"
          checkboxClassNames={
            form.getFieldState("acceptedTerms").error
              ? "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background rounded-[5px] ring-1 focus-visible:ring-offset-1"
              : undefined
          }
          labelContent={<TermsLabel isProfessional={isProfessional} />}
        />
        {(isSignupError || isSignupIndividualError) && (
          <p className="text-red-600">
            {form.formState.errors.email?.message ?? "Une erreur est survenue"}
          </p>
        )}
        <div className="flex w-full flex-col gap-3">
          <Button type="submit" variant="blue" size="lg">
            Créer mon compte
          </Button>
          <Button
            type="button"
            className="shadow-none"
            size="lg"
            onClick={() => onSwitchSteps("LOGIN")}
          >
            J&apos;ai déjà un compte
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default Signup;
